import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { Phone } from "./Frames";
import { APP_SCREENS } from "./AppScreens";
import { PortalMock } from "./PortalMock";

const PORTAL_STEPS = [
  { tier: "classico", qty: 20, logo: false, title: "Scelta del cesto", text: "L'azienda confronta tre composizioni con prezzo per dipendente sempre visibile." },
  { tier: "premium", qty: 40, logo: true, title: "Personalizzazione", text: "Logo su biglietto e nastro: il preventivo si aggiorna all'istante." },
  { tier: "premium", qty: 80, logo: true, title: "Sconto volume automatico", text: "Oltre 50 pezzi lo sconto si applica da solo, senza trattative." },
  { tier: "luxury", qty: 120, logo: true, title: "Conferma in un clic", text: "Il cliente blocca data e prezzo; l'ordine entra nel piano del laboratorio." },
];

const SCENES = [
  ...APP_SCREENS.map((s) => ({ kind: "app", ...s })),
  ...PORTAL_STEPS.map((s, i) => ({ kind: "portal", key: `portal-${i}`, ...s })),
];
const MS = 4200;

const Progress = ({ idx, playing }) => (
  <div className="flex gap-1.5 mt-6">
    {SCENES.map((s, i) => (
      <span key={s.key} className="demo-bar">
        <i className={i < idx ? "demo-bar--done" : i === idx ? (playing ? "demo-bar--run" : "demo-bar--done") : ""} style={i === idx ? { animationDuration: `${MS}ms` } : undefined} />
      </span>
    ))}
  </div>
);

const nextScene = (current) => (current + 1) % SCENES.length;

const useInView = (ref) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
};

const Scene = ({ s, i, active, playing }) => (
  <div className={`demo-scene ${active ? "demo-scene--on" : ""}`} aria-hidden={!active}>
    <div className={`demo-stage ${s.kind === "portal" ? "demo-stage--wide" : ""}`}>
      <div className="demo-visual">
        {s.kind === "app" ? <Phone testId={active ? "demo-phone" : undefined}><s.C /></Phone> : <PortalMock testId={active ? "demo-portal" : undefined} tier={s.tier} qty={s.qty} logo={s.logo} />}
      </div>
      <div className="demo-caption">
        <div className="kicker !text-[var(--gold-light)]">{s.kind === "app" ? "App Clienti" : "Portale Aziende"} · {i + 1}/{SCENES.length}</div>
        <div className="font-display text-3xl text-[var(--cream)] mt-2" data-testid={active ? "demo-title" : undefined}>{s.title}</div>
        <p className="text-[15px] leading-relaxed text-[rgba(247,241,230,0.78)] mt-3">{s.text}</p>
        <Progress idx={i} playing={active && playing} />
      </div>
    </div>
  </div>
);

export const AppDemo = () => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const ref = useRef(null);
  const inView = useInView(ref);
  const running = playing && inView;
  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => setIdx(nextScene), MS);
    return () => clearTimeout(t);
  }, [idx, running]);
  const s = SCENES[idx];
  const go = (kind) => setIdx(SCENES.findIndex((x) => x.kind === kind));

  return (
    <div ref={ref} className="demo pdf-hide" data-testid="app-demo">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2">
          {[["app", "App Clienti"], ["portal", "Portale Aziende"]].map(([k, l]) => (
            <button key={k} data-testid={`demo-tab-${k}`} onClick={() => go(k)} className={`demo-tab ${s.kind === k ? "demo-tab--on" : ""}`}>{l}</button>
          ))}
        </div>
        <button data-testid="demo-play-toggle" onClick={() => setPlaying((p) => !p)} className="demo-tab flex items-center gap-2">
          {playing ? <Pause size={14} /> : <Play size={14} />} {playing ? "Pausa" : "Riproduci"}
        </button>
      </div>
      <div className="demo-scenes">
        {SCENES.map((sc, i) => <Scene key={sc.key} s={sc} i={i} active={i === idx} playing={running} />)}
      </div>
    </div>
  );
};
